// HSL city bikes belong to the "smoove" rental network in Digitransit.
export const DEFAULT_RENTAL_NETWORK = "smoove";

// Digitransit v2 station ids are in the format network:id (e.g. smoove:019).
// Plain ids (e.g. 019) from older bookmarks and saved state get the HSL
// network prefix so they keep working.
export const normalizeStationId = (stationId: string) => {
    const id = stationId.trim();
    return id.includes(":") ? id : `${DEFAULT_RENTAL_NETWORK}:${id}`;
};

// Shorter ids for shareable links: smoove:019 -> 019.
export const toShortStationId = (stationId: string) =>
    stationId.startsWith(`${DEFAULT_RENTAL_NETWORK}:`)
        ? stationId.slice(DEFAULT_RENTAL_NETWORK.length + 1)
        : stationId;

export const getWalkingDirectionsUrl = (lat: number, lon: number) => {
    const destination = encodeURIComponent(`${lat},${lon}`);
    return `https://www.google.com/maps/dir/?api=1&destination=${destination}&travelmode=walking`;
};

export const getShareUrl = (stationIds: Array<string>, baseUrl: string) => {
    const url = new URL(baseUrl);
    url.search = "";
    url.hash = "";
    url.searchParams.set("id", stationIds.map(toShortStationId).join(","));
    // Keep the commas readable in the shared link.
    return url.toString().replace(/%2C/g, ",");
};

export type Availability = "good" | "low" | "empty" | "closed";

export const getAvailability = (
    bikesAvailable: number,
    operative: boolean
): Availability => {
    if (!operative) return "closed";
    if (bikesAvailable <= 0) return "empty";
    if (bikesAvailable <= 2) return "low";
    return "good";
};

// 250 m, 1,2 km (in the given locale's number format).
export const formatDistance = (km: number, locale: string) => {
    if (km < 1) {
        const meters = Math.max(10, Math.round((km * 1000) / 10) * 10);
        return `${meters} m`;
    }
    const formatted = new Intl.NumberFormat(locale, {
        maximumFractionDigits: km < 10 ? 1 : 0,
    }).format(km);
    return `${formatted} km`;
};

export function getDistanceFromLatLonInKm(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
) {
    return haversine(lat1, lon1, lat2, lon2);
}

// Radius of the earth in km
const EARTH_RADIUS = 6371;

// Returns distance in km using haversine formula
function haversine(lat1: number, lon1: number, lat2: number, lon2: number) {
    let dLat = degToRad(lat2 - lat1);
    let dLon = degToRad(lon2 - lon1);
    let a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(degToRad(lat1)) *
            Math.cos(degToRad(lat2)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
    let c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    let d = EARTH_RADIUS * c;
    return d;
}

function degToRad(deg: number) {
    return deg * (Math.PI / 180);
}
